import { useEffect, useState } from "react";
import { CalendarClock } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { ETAPAS, PRIORIDADES, STATUS_TAREFA } from "@/lib/helpers";
import { PRODUCTION_COLUMNS, PLANNING_CONDITIONS, UNDATED_CONDITIONS, taskColumn } from "@/lib/productionTable";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const formDaTarefa = (tarefa) => tarefa ? {
  titulo: tarefa.titulo || "",
  descricao: tarefa.descricao || "",
  responsavel: tarefa.responsavel || "",
  responsavel_usuario_id: tarefa.responsavel_usuario_id || "",
  agenda_hora_inicio: tarefa.agenda_hora_inicio || "",
  agenda_hora_fim: tarefa.agenda_hora_fim || "",
  prazo: tarefa.prazo || "",
  status: tarefa.status || "nao_iniciado",
  prioridade: tarefa.prioridade || "media",
  etapa: tarefa.etapa || "planejamento",
  planejamento_coluna: taskColumn(tarefa),
  planejamento_condicao: tarefa.prazo && UNDATED_CONDITIONS.has(tarefa.planejamento_condicao) ? "" : tarefa.planejamento_condicao || (tarefa.prazo ? "" : "a_agendar"),
} : null;

export function TarefaEditDialog({ tarefa, equipe = [], onOpenChange, onSaved }) {
  const [form, setForm] = useState(() => formDaTarefa(tarefa));
  const [salvando, setSalvando] = useState(false);
  const [pessoas, setPessoas] = useState([]);
  const [pessoasErro, setPessoasErro] = useState("");

  useEffect(() => {
    setForm(formDaTarefa(tarefa));
  }, [tarefa]);

  useEffect(() => {
    if (!tarefa) return;
    let active = true;
    setPessoasErro("");
    api.get("/agenda-pessoal/responsaveis").then(({ data }) => { if (active) setPessoas(data); })
      .catch(() => { if (active) setPessoasErro("Não foi possível carregar as pessoas. O vínculo atual será preservado."); });
    return () => { active = false; };
  }, [tarefa]);

  const fechar = () => onOpenChange?.(false);

  const salvar = async (event) => {
    event.preventDefault();
    if (!tarefa || !form?.titulo.trim()) return;
    if (form.planejamento_condicao === "proposta" && !form.prazo) { toast.error("Informe a data proposta"); return; }
    setSalvando(true);
    try {
      const response = await api.patch(`/tarefas/${tarefa.id}`, form);
      toast.success("Atividade atualizada em todas as telas");
      onSaved?.(response.data);
      fechar();
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Dialog open={!!tarefa} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto border-[#27272A] bg-[#141418] text-[#F4F4F0]">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Editar <span className="gold-italic">atividade</span></DialogTitle>
          <DialogDescription className="sr-only">Altere os dados e a data desta atividade sem sair da tela atual.</DialogDescription>
        </DialogHeader>
        {tarefa && form && (
          <form onSubmit={salvar} className="mt-2 space-y-4" data-testid="tarefa-edit-form">
            {(tarefa.cliente_empresa || tarefa.ciclo_titulo) && (
              <p className="caption-label normal-case tracking-normal">
                {[tarefa.cliente_empresa, tarefa.ciclo_titulo].filter(Boolean).join(" · ")}
              </p>
            )}
            <div>
              <Label className="caption-label">Título</Label>
              <Input required data-testid="tarefa-edit-titulo" value={form.titulo} onChange={(event) => setForm({ ...form, titulo: event.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" />
            </div>
            <div>
              <Label className="caption-label">Descrição</Label>
              <Textarea data-testid="tarefa-edit-descricao" value={form.descricao} onChange={(event) => setForm({ ...form, descricao: event.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" rows={3} />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label className="caption-label">Responsável</Label>
                <Select value={form.responsavel || "none"} onValueChange={(value) => setForm({ ...form, responsavel: value === "none" ? "" : value })}>
                  <SelectTrigger data-testid="tarefa-edit-responsavel" className="mt-1.5 bg-[#18181C] border-[#27272A]"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
                    <SelectItem value="none">—</SelectItem>
                    {equipe.map((membro) => <SelectItem key={membro.id} value={membro.nome}>{membro.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="caption-label">Data da atividade</Label>
                <Input data-testid="tarefa-edit-prazo" type="date" value={form.prazo} disabled={UNDATED_CONDITIONS.has(form.planejamento_condicao)} required={form.planejamento_condicao === "proposta"} onChange={(event) => setForm({ ...form, prazo: event.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" />
              </div>
              <div>
                <Label className="caption-label">Status</Label>
                <Select value={form.status} onValueChange={(value) => setForm({ ...form, status: value })}>
                  <SelectTrigger data-testid="tarefa-edit-status" className="mt-1.5 bg-[#18181C] border-[#27272A]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
                    {Object.entries(STATUS_TAREFA).map(([key, item]) => <SelectItem key={key} value={key}>{item.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="caption-label">Prioridade</Label>
                <Select value={form.prioridade} onValueChange={(value) => setForm({ ...form, prioridade: value })}>
                  <SelectTrigger data-testid="tarefa-edit-prioridade" className="mt-1.5 bg-[#18181C] border-[#27272A]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
                    {Object.entries(PRIORIDADES).map(([key, item]) => <SelectItem key={key} value={key}>{item.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {form.status === "cancelado" && <p className="text-sm text-[#A1A1AA]">A atividade sai das pendências e permanece no histórico do ciclo. Ela não conta como concluída.</p>}
            <div>
              <Label className="caption-label">Etapa de produção</Label>
              <Select value={form.etapa} onValueChange={(value) => { const currentColumn = PRODUCTION_COLUMNS.find((c) => c.key === form.planejamento_coluna); const nextColumn = currentColumn?.etapa === value ? currentColumn.key : PRODUCTION_COLUMNS.find((c) => c.etapa === value)?.key || "outros"; setForm({ ...form, etapa: value, planejamento_coluna: nextColumn }); }}>
                <SelectTrigger data-testid="tarefa-edit-etapa" className="mt-1.5 bg-[#18181C] border-[#27272A]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
                  {ETAPAS.map((item) => <SelectItem key={item.key} value={item.key}>{item.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="tarefa-coluna" className="caption-label">Coluna por cliente</Label>
                <select id="tarefa-coluna" data-testid="tarefa-edit-coluna" className="mt-1.5 w-full rounded-md border border-[#27272A] bg-[#18181C] px-3 py-2 text-sm" value={form.planejamento_coluna}
                  onChange={(event) => { const column = PRODUCTION_COLUMNS.find((c) => c.key === event.target.value); setForm({ ...form, planejamento_coluna: column.key, ...(column.key !== "outros" ? { etapa: column.etapa } : {}) }); }}>
                  {PRODUCTION_COLUMNS.map((column) => <option key={column.key} value={column.key}>{column.label}</option>)}
                </select>
              </div>
              <div>
                <Label htmlFor="tarefa-planejamento" className="caption-label">Planejamento</Label>
                <select id="tarefa-planejamento" data-testid="tarefa-edit-planejamento" className="mt-1.5 w-full rounded-md border border-[#27272A] bg-[#18181C] px-3 py-2 text-sm" value={form.planejamento_condicao}
                  onChange={(event) => setForm({ ...form, planejamento_condicao: event.target.value, ...(UNDATED_CONDITIONS.has(event.target.value) ? { prazo: "", agenda_hora_inicio: "", agenda_hora_fim: "" } : {}) })}>
                  {PLANNING_CONDITIONS.map((condition) => <option key={condition.key} value={condition.key}>{condition.label}</option>)}
                </select>
              </div>
            </div>
            <fieldset className="space-y-3 rounded-lg border border-border p-3">
              <legend className="px-1 text-sm font-semibold">Agenda individual · piloto</legend>
              <Label htmlFor="tarefa-agenda-pessoa">Pessoa responsável pela atividade</Label>
              <select id="tarefa-agenda-pessoa" data-testid="tarefa-agenda-pessoa" disabled={!!pessoasErro}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                value={form.responsavel_usuario_id} onChange={(e) => setForm({ ...form, responsavel_usuario_id: e.target.value })}>
                <option value="">Sem agenda individual</option>
                {form.responsavel_usuario_id && !pessoas.some((p) => p.id === form.responsavel_usuario_id) && <option value={form.responsavel_usuario_id}>Responsável já vinculado</option>}
                {pessoas.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
              </select>
              {pessoasErro && <p role="alert" className="text-xs text-destructive">{pessoasErro}</p>}
              <p className="text-xs text-muted-foreground">A função ou nome acima não escolhe uma agenda automaticamente. Selecione a pessoa para vincular esta tarefa à conta Google dela.</p>
              <div className="grid grid-cols-2 gap-3">
                <div><Label htmlFor="tarefa-agenda-inicio">Início (opcional)</Label><Input id="tarefa-agenda-inicio" type="time" disabled={!form.prazo} value={form.agenda_hora_inicio} onChange={(e) => setForm({ ...form, agenda_hora_inicio: e.target.value, ...(!e.target.value ? { agenda_hora_fim: "" } : {}) })} /></div>
                <div><Label htmlFor="tarefa-agenda-fim">Fim (opcional)</Label><Input id="tarefa-agenda-fim" type="time" disabled={!form.prazo || !form.agenda_hora_inicio} value={form.agenda_hora_fim} onChange={(e) => setForm({ ...form, agenda_hora_fim: e.target.value })} /></div>
              </div>
              <p className="text-xs text-muted-foreground">Sem horário: prazo como dia inteiro. Compromissos já vinculados usam o horário da gravação/reunião. Nenhum convite será enviado.</p>
            </fieldset>
            <div className="flex gap-3 rounded-lg border border-[#8CC63F]/25 bg-[#8CC63F]/5 px-3 py-2.5">
              <CalendarClock size={16} className="mt-0.5 shrink-0 text-[#8CC63F]" />
              <p className="text-xs leading-relaxed text-[#A1A1AA]">A nova data será refletida no ciclo e, quando esta atividade representar reunião, gravação ou entrega, também no Google Agenda conectado.</p>
            </div>
            <Button data-testid="tarefa-edit-salvar" type="submit" disabled={salvando} className="w-full rounded-full bg-[#8CC63F] font-semibold text-[#09090B] hover:bg-[#A9DD6B]">
              {salvando ? "Salvando…" : "Salvar atividade"}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
